import React from 'react';
import { useLocation } from 'react-router-dom';

import { useMobileNavigate } from './MobileNavigationGuard';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { cn } from '../../lib/utils';
import { MOBILE_NAV_ITEMS, isNavItemActive } from './navigation';
import { MobileRecentRecords } from './MobileRecentRecords';
import { Button } from '../ui/button';

type MobileMenuOverlayProps = {
  open: boolean;
  onClose: () => void;
  returnFocusRef?: React.RefObject<HTMLButtonElement>;
};

export function MobileMenuOverlay({ open, onClose, returnFocusRef }: MobileMenuOverlayProps) {
  const navigate = useMobileNavigate();
  const [recents, setRecents] = React.useState(false);
  const recentButton = React.useRef<HTMLButtonElement>(null);
  const returnFocus = React.useRef<HTMLElement | null>(null);
  const location = useLocation();


  const handleNavigate = (to: string) => {
    navigate(to);
    onClose();
  };

  return (
    <>
    {recents && <MobileRecentRecords onClose={() => setRecents(false)} onNavigate={() => { setRecents(false); onClose(); }} onRestoreFocus={() => (recentButton.current ?? returnFocusRef?.current)?.focus()} />}
    <Dialog open={open && !recents} onOpenChange={(value) => { if (!value) onClose(); }}>
      <DialogContent aria-labelledby="mobile-navigation-title" onKeyDown={(event) => {
        if (event.key === 'Escape') { event.preventDefault(); onClose(); }
      }} onOpenAutoFocus={() => { returnFocus.current = document.activeElement as HTMLElement; }} onCloseAutoFocus={(event) => { event.preventDefault(); if (!recents) (returnFocusRef?.current ?? returnFocus.current)?.focus(); }} className="max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle id="mobile-navigation-title">Quick navigation</DialogTitle>
          <DialogDescription>Choose a workspace.</DialogDescription>
        </DialogHeader>
        <Button variant="ghost" className="min-h-11" onClick={onClose}>Close</Button>
        <Button ref={recentButton} variant="outline" className="min-h-11" onClick={() => setRecents(true)}>Recent Records</Button>
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
          {MOBILE_NAV_ITEMS.map((item) => {
            const active = isNavItemActive(location.pathname, item);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleNavigate(item.to)}
                className={cn(
                  'group flex flex-col items-center gap-1 rounded-xl border border-border bg-card p-3 text-center shadow-sm transition hover:border-primary/60 hover:shadow-md',
                  active && 'border-primary/70 ring-2 ring-primary/10'
                )}
                aria-label={item.label}
              >
                <span
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground transition',
                    active && 'bg-primary/10 text-primary'
                  )}
                >
                  <item.icon className={cn('h-5 w-5', active ? 'stroke-[2.2]' : 'stroke-[1.8]')} />
                </span>
                <span className="text-xs font-semibold text-foreground">{item.label}</span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
